import { useClickAnimation } from '../hooks/use-click-animation'
import { Tooltip, TooltipTrigger, TooltipPopup } from './tooltip'
import { cn } from '../lib/utils'
import {
  findIcon,
  extractTextContent,
  processChildrenTree,
} from '../lib/children-utils'
import { LoadingContent } from '../lib/loading-content'
import { Button as BaseButton } from '@base-ui/react/button'
import { cva } from 'class-variance-authority'
import { isValidElement } from 'react'
import type * as React from 'react'

const fabVariants = cva(
  'focus-visible:ring-primary/80 focus-visible:ring-offset-surface-1 inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-2xl p-4 outline-none select-none focus-visible:ring-4 focus-visible:ring-offset-4 disabled:cursor-not-allowed [@media(hover:none)]:disabled:pointer-events-none',
  {
    variants: {
      semanticRole: {
        primary: 'bg-primary text-on-primary',
        secondary: 'bg-secondary text-on-secondary',
      },
    },
    defaultVariants: {
      semanticRole: 'primary',
    },
  },
)

interface FabProps extends BaseButton.Props {
  semanticRole?: 'primary' | 'secondary'
  loading?: boolean
  /** When true, shows both icon and label. Default (false) shows icon only with label as tooltip. */
  extended?: boolean
}

function Fab({
  className,
  semanticRole,
  loading,
  disabled,
  extended,
  children,
  render,
  nativeButton,
  ...props
}: FabProps) {
  const clickAnimation = useClickAnimation()

  const [icon, labelText, originalChildren] =
    validateAndExtractChildren(children)
  const processedChildren = processChildrenTree(originalChildren, {})

  const fabElement = (
    <BaseButton
      className={cn(
        fabVariants({
          semanticRole,
          className,
        }),
        loading ? '!cursor-wait' : 'disabled:opacity-60',
        clickAnimation.className,
      )}
      data-slot="fab"
      data-testid={labelText}
      // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- intentional: boolean || boolean, disabled can be false
      disabled={disabled || loading}
      render={render}
      nativeButton={
        nativeButton ?? !(isValidElement(render) && render.type === 'a')
      }
      {...clickAnimation.handlers}
      {...props}
    >
      <LoadingContent loading={loading}>
        {extended ? processedChildren : icon}
      </LoadingContent>
    </BaseButton>
  )

  if (!extended) {
    return (
      <Tooltip>
        <TooltipTrigger render={fabElement} />
        <TooltipPopup>{labelText}</TooltipPopup>
      </Tooltip>
    )
  }

  return fabElement
}

function validateAndExtractChildren(children: React.ReactNode) {
  const icon = findIcon(children)
  const labelText = extractTextContent(children)

  if (!icon) throw new Error('Fab requires an icon - you missed it.')
  if (!labelText) {
    throw new Error(
      'Fab requires text content for accessibility - you missed it. Text can be direct or wrapped in elements.',
    )
  }

  return [icon, labelText, children] as const
}

Fab.renderable = true

export { Fab }
